import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { identityGateway } from '../../identity/services';
import type { MfaEnrollmentResponse } from '../../identity/types';
import { useIdentity } from '../../identity/state/useIdentity';
import { AuthenticatorSection } from '../../security/pages/SecurityWorkspacePage';
import { useStepUpGuard } from '../../security/components/useStepUpGuard';
import type { PartnerLoad } from '../state/PartnerProvider';
import '../partnerPortal.css';

const NO_ACCESS = 'You do not currently have access to this portal.';

function Centered({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="pp-centered">
      <section className="pp-card pp-card--narrow" aria-labelledby="pp-gate-title">
        <h1 id="pp-gate-title">{title}</h1>
        {children}
      </section>
    </div>
  );
}

function SignOutButton() {
  const { logout } = useIdentity();
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="pp-button"
      onClick={async () => {
        await logout();
        navigate('/partner/sign-in', { replace: true });
      }}
    >
      Sign out
    </button>
  );
}

/** What the portal shows before any partner page: loading, a failure, "no access", or the mandatory MFA step. Chosen from what the server reported; the server enforces all of it again. */
export function PartnerGateScreens({ load }: { load: PartnerLoad }) {
  if (load.kind === 'loading') {
    return (
      <div className="pp-centered">
        <p className="pp-muted">Loading…</p>
      </div>
    );
  }

  if (load.kind === 'error') {
    return (
      <Centered title="Partner Portal">
        <p role="alert">{load.message}</p>
        <SignOutButton />
      </Centered>
    );
  }

  if (load.me.memberships.length === 0) {
    // The same words whether the person has never been a partner or their firm no longer exists; nothing here says which, or whether any account or firm exists.
    return (
      <Centered title="Partner Portal">
        <p role="alert">{NO_ACCESS}</p>
        <p className="pp-muted">If you expected access, contact the person who manages your firm's Escochex account.</p>
        <SignOutButton />
      </Centered>
    );
  }

  return <MfaStep />;
}

/** MFA is mandatory for every partner user. The session must be verified with an authenticator app; a person who has none enrolls one here, then signs in again and verifies. */
function MfaStep() {
  const { guard, dialog } = useStepUpGuard();
  const [enrollment, setEnrollment] = useState<MfaEnrollmentResponse | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    try {
      const list = await identityGateway.listMfaEnrollments();
      setEnrollment(list.find((e) => e.method === 'Authenticator' && e.isEnabled) ?? null);
    } catch {
      setError('Your verification settings could not be loaded.');
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the first load on mount, the same pattern SecurityWorkspacePage uses
    void load();
  }, []);

  return (
    <Centered title="Verify with your authenticator app">
      <p>Partner accounts must be verified with an authenticator app every time they sign in. This protects your clients' payroll information.</p>
      {error && <p role="alert">{error}</p>}
      {enrollment === undefined && !error && <p className="pp-muted">Checking…</p>}
      {enrollment === null && (
        <>
          <p>Set up your authenticator app first.</p>
          <AuthenticatorSection enrollment={null} onChanged={load} guard={guard} />
        </>
      )}
      {enrollment && <p className="pp-ok">Your authenticator app is set up.</p>}
      {enrollment && (
        <p>Sign out, then sign in again and enter the 6-digit code from your authenticator app when asked.</p>
      )}
      <SignOutButton />
      {dialog}
    </Centered>
  );
}
